import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFullscreen } from '@/contexts/FullscreenContext';
import { getBaseUrl } from '@/services/httpClient';
import { playAudioSafely } from '@/services/iosAudioHelper';

/**
 * JourneyBreathingCycle - ÉCRAN 2 : Breathing (cercle respiratoire)
 * 
 * DISCOVER HEAREN - BUILD 50 SPECS (STRICT):
 * - Voix Hera UNE SEULE FOIS au début: "Inspirez… lentement. Et relâchez."
 * - Après cette phrase: PLUS AUCUNE VOIX
 * - Seulement Calm_031.mp3 qui continue (depuis JourneyIntro)
 * - Ne pas guider les respirations suivantes
 */
const JourneyBreathingCycle = () => {
  const navigate = useNavigate();
  const { setIsFullscreen } = useFullscreen();
  const [phase, setPhase] = useState('exhale'); // Commencer petit pour grandir ensuite
  const [showCTA, setShowCTA] = useState(false);
  const heraPlayedRef = useRef(false); // Empêcher la répétition de la voix
  
  const isEnglish = localStorage.getItem('i18nextLng')?.includes('en') ||
                    localStorage.getItem('hearen_lang') === 'en';

  useEffect(() => {
    setIsFullscreen(true);
    
    const baseUrl = getBaseUrl();
    const lang = isEnglish ? 'en' : 'fr';
    
    // === VOIX HERA - UNE SEULE FOIS AU DÉBUT ===
    // "Inspirez… lentement. Et relâchez."
    // IMPORTANT: Attendre que le message d'intro (JourneyIntro) soit TERMINÉ
    // puis attendre 1400ms supplémentaires avant de jouer ce message
    const playHeraOnce = async () => {
      if (heraPlayedRef.current) return;
      heraPlayedRef.current = true;
      
      const heraUrl = `${baseUrl}/api/voice-files/discover_breathe_${lang}.mp3`;
      console.log(`[Discover][Breathing] 🎙️ Playing Hera ONCE: ${heraUrl}`);
      
      // V2: Réutiliser l'objet Audio global déverrouillé dans JourneyIntro
      const heraAudio = window.discoverVoiceAudio || new Audio();
      heraAudio.src = heraUrl;
      heraAudio.volume = 0.8;
      heraAudio.preload = 'auto';
      
      // V2: Use playAudioSafely for iOS compatibility
      const success = await playAudioSafely(heraAudio, 'Discover Hera Breathe');
      if (success) {
        console.log('[Discover][Breathing] ✅ Hera voice played (will NOT repeat)');
      } else {
        console.error('[Discover][Breathing] ❌ Hera voice blocked');
      }
    };
    
    // Fonction qui attend la fin du message d'intro puis joue le message de respiration
    const waitForIntroEndAndPlay = () => {
      // Si le flag est déjà true, attendre 1400ms puis jouer
      if (window.discoverIntroVoiceEnded === true) {
        console.log('[Discover][Breathing] ✅ Intro voice already ended - waiting 1400ms before breathing voice');
        setTimeout(playHeraOnce, 1400);
        return;
      }
      
      // Sinon, vérifier périodiquement (toutes les 100ms)
      console.log('[Discover][Breathing] ⏳ Waiting for intro voice to end...');
      const checkInterval = setInterval(() => {
        if (window.discoverIntroVoiceEnded === true) {
          clearInterval(checkInterval);
          console.log('[Discover][Breathing] ✅ Intro voice ended - waiting 1400ms before breathing voice');
          setTimeout(playHeraOnce, 1400);
        }
      }, 100);
      
      // Timeout de sécurité: si après 10 secondes le flag n'est toujours pas true, on joue quand même
      setTimeout(() => {
        clearInterval(checkInterval);
        if (!heraPlayedRef.current) {
          console.log('[Discover][Breathing] ⚠️ Safety timeout - playing breathing voice anyway');
          playHeraOnce();
        }
      }, 10000);
    };
    
    // Petit délai pour laisser la transition se faire, puis démarrer
    const startTimer = setTimeout(() => {
      setPhase('inhale');
      // Attendre la fin du message d'intro avant de jouer le message de respiration
      waitForIntroEndAndPlay();
    }, 100);
    
    // Cycle respiratoire SILENCIEUX (pas de voix, juste l'animation)
    let currentPhase = 'inhale';
    const breathingInterval = setInterval(() => {
      if (currentPhase === 'inhale') {
        currentPhase = 'exhale';
        setPhase('exhale');
        // PAS DE VOIX - seulement animation silencieuse
      } else {
        currentPhase = 'inhale';
        setPhase('inhale');
        // PAS DE VOIX - seulement animation silencieuse
      }
    }, 4000);

    // Afficher le CTA après le premier cycle complet (8 secondes)
    const ctaTimer = setTimeout(() => {
      setShowCTA(true);
    }, 8100);

    return () => {
      clearTimeout(startTimer);
      clearInterval(breathingInterval);
      clearTimeout(ctaTimer);
      setIsFullscreen(false);
    };
  }, [setIsFullscreen, isEnglish]);

  const handleContinue = () => {
    console.log('[Discover][Breathing] ➡️ Continuing to next screen');
    navigate('/journey-respiration-preview');
  };

  return (
    <div 
      className="min-h-screen relative overflow-hidden flex flex-col items-center justify-between px-6 py-12"
      style={{
        background: 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)',
        animation: 'breathing-glow 8s ease-in-out infinite',
      }}
    >
      {/* Halo externe pulsant */}
      <div
        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rounded-full"
        style={{
          width: phase === 'inhale' ? '500px' : '300px',
          height: phase === 'inhale' ? '500px' : '300px',
          background: 'radial-gradient(circle, rgba(16, 185, 129, 0.15) 0%, transparent 70%)',
          filter: 'blur(80px)',
          transition: 'all 4s cubic-bezier(0.4, 0, 0.2, 1)',
          pointerEvents: 'none',
        }}
      />
      
      {/* Orbe centrale animée */}
      <div
        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rounded-full"
        style={{
          width: phase === 'inhale' ? '350px' : '200px',
          height: phase === 'inhale' ? '350px' : '200px',
          background: 'radial-gradient(circle, rgba(255, 255, 255, 0.35) 0%, rgba(16, 185, 129, 0.25) 50%, transparent 70%)',
          filter: 'blur(50px)',
          transition: 'all 4s cubic-bezier(0.4, 0, 0.2, 1)',
          pointerEvents: 'none',
        }}
      />

      {/* Bloc titre en haut */}
      <div className="relative z-10 text-center max-w-lg" style={{ animation: 'fade-in-up 600ms ease-out' }}>
        <h2 className="text-3xl font-bold text-white mb-3 tracking-wide">
          {isEnglish ? 'Breathe with Hearen' : 'Respirez avec Hearen'}
        </h2>
        <p className="text-white/90 text-base leading-relaxed">
          {isEnglish 
            ? 'A short guided breath to help you enter a calmer state.'
            : 'Une courte respiration guidée pour vous aider à entrer dans le calme.'
          }
        </p>
      </div>

      {/* Orbe et textes dynamiques au centre */}
      <div className="relative z-10 flex flex-col items-center">
        {/* Orbe animée */}
        <div
          className="w-48 h-48 rounded-full flex items-center justify-center mb-16 relative"
          style={{
            background: 'rgba(255, 255, 255, 0.25)',
            backdropFilter: 'blur(15px)',
            border: '3px solid rgba(255, 255, 255, 0.4)',
            transform: phase === 'inhale' ? 'scale(1.12)' : 'scale(1)',
            transition: 'transform 4s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: phase === 'inhale' 
              ? '0 0 60px rgba(16, 185, 129, 0.7), 0 12px 40px rgba(16, 185, 129, 0.5)' 
              : '0 8px 32px rgba(16, 185, 129, 0.3)',
          }}
        >
          <div
            className="w-24 h-24 rounded-full"
            style={{
              background: 'rgba(255, 255, 255, 0.6)',
              boxShadow: '0 0 30px rgba(255, 255, 255, 0.6)',
              animation: 'inner-pulse 2s ease-in-out infinite',
            }}
          />
        </div>

        {/* Texte dynamique - Inspirez */}
        <p 
          className="text-white text-2xl font-medium mb-8 text-center"
          style={{
            opacity: phase === 'inhale' ? 1 : 0,
            transition: 'opacity 1s ease-in-out',
          }}
        >
          {isEnglish ? 'Breathe in...' : 'Inspirez...'}
        </p>
        
        {/* Texte dynamique - Expirez */}
        <p 
          className="text-white text-2xl font-medium mb-8 text-center"
          style={{
            opacity: phase === 'exhale' ? 1 : 0,
            transition: 'opacity 1s ease-in-out',
            position: 'absolute',
            top: '50%',
            marginTop: '12rem',
          }}
        >
          {isEnglish ? 'Breathe out...' : 'Expirez...'}
        </p>
      </div>

      {/* Bloc guidance + CTA en bas */}
      <div className="relative z-10 flex flex-col items-center max-w-lg" style={{ animation: 'fade-in-up 600ms ease-out 200ms both' }}>
        <p className="text-white/80 text-sm text-center mb-6 leading-relaxed">
          {isEnglish 
            ? 'Follow the motion of the circle. Inhale… exhale… gently.'
            : 'Suivez le mouvement du cercle. Inspirez… Expirez… tout simplement.'
          }
        </p>

        {showCTA && (
          <button
            onClick={handleContinue}
            className="px-10 py-4 bg-white text-emerald-700 rounded-full font-semibold text-lg transition-all hover:scale-105 active:scale-95"
            style={{
              boxShadow: '0 0 30px rgba(255, 255, 255, 0.4)',
              animation: 'fade-in 700ms ease-out',
            }}
          >
            {isEnglish ? 'Discover Breathing' : 'Découvrir la Respiration'}
          </button>
        )}
      </div>

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes breathing-glow {
          0%, 100% { filter: brightness(1); }
          50% { filter: brightness(1.1); }
        }
        
        @keyframes inner-pulse {
          0%, 100% { transform: scale(1); opacity: 0.5; }
          50% { transform: scale(1.15); opacity: 0.8; }
        }
      `}</style>
    </div>
  );
};

export default JourneyBreathingCycle;
